import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { apiClient, formatApiErrorDetail, API } from "../lib/api";

function ListInput({ label, value, onChange, placeholder, testId }) {
  // value is array of strings; edited as one-per-line textarea
  const text = (value || []).join("\n");
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Textarea
        value={text}
        onChange={(e) => onChange(e.target.value.split("\n").map((s) => s).filter((s, i, arr) => i < arr.length - 1 || s !== ""))}
        rows={4}
        placeholder={placeholder || "One item per line"}
        className="text-sm"
        data-testid={testId}
      />
      <p className="text-xs text-muted-foreground">One item per line.</p>
    </div>
  );
}

function StructureInput({ value, onChange, testId }) {
  const items = value || [];
  const update = (i, key, val) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, [key]: val } : x));
    onChange(next);
  };
  const add = () => onChange([...items, { title: "", description: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testId}>
      <Label>Course structure (modules)</Label>
      {items.map((m, i) => (
        <div key={i} className="space-y-1 rounded-lg border border-[#e8e0f0] bg-white/70 p-2">
          <Input value={m.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="Module title" />
          <Textarea value={m.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Module description" rows={2} />
          <div className="flex justify-end">
            <button type="button" onClick={() => remove(i)} className="text-xs text-red-700 hover:underline">Remove module</button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={add}>
        Add module
      </Button>
    </div>
  );
}

function DetailInput({ value, onChange }) {
  const items = value || [];
  const update = (i, key, val) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, [key]: val } : x));
    onChange(next);
  };
  const add = () => onChange([...items, { heading: "", content: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <Label>Detail sections</Label>
      {items.map((m, i) => (
        <div key={i} className="space-y-1 rounded-lg border border-[#e8e0f0] bg-white/70 p-2">
          <Input value={m.heading} onChange={(e) => update(i, "heading", e.target.value)} placeholder="Heading" />
          <Textarea value={m.content} onChange={(e) => update(i, "content", e.target.value)} placeholder="Content" rows={3} />
          <div className="flex justify-end">
            <button type="button" onClick={() => remove(i)} className="text-xs text-red-700 hover:underline">Remove section</button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={add}>
        Add section
      </Button>
    </div>
  );
}

function ImageUploadField({ label, value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
      onChange(data.url);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  const preview = value
    ? (value.startsWith("http") ? value : `${API.replace(/\/api$/, "")}${value}`)
    : null;
  return (
    <div className="space-y-1" data-testid={testId}>
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {preview ? (
          <img src={preview} alt="" className="h-16 w-16 rounded-full border border-[#e8e0f0] object-cover" />
        ) : (
          <div className="h-16 w-16 rounded-full border border-dashed border-[#d4c5e6] bg-[#f5f0fa]" />
        )}
        <div className="space-y-1">
          <label className="inline-flex cursor-pointer rounded-md border border-[#d4c5e6] bg-white px-3 py-1 text-xs font-medium text-[#361a54] hover:bg-[#f5f0fa]">
            {busy ? "Uploading…" : (value ? "Replace photo" : "Upload photo")}
            <input type="file" accept="image/*" className="hidden" disabled={busy}
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {value && (
            <button type="button" className="block text-xs text-red-700 hover:underline" onClick={() => onChange("")}>
              Remove
            </button>
          )}
        </div>
      </div>
      {err && <p className="text-xs text-red-700">{err}</p>}
    </div>
  );
}

const COURSE_SECTION_LABELS = {
  key_highlights: "Key Highlights",
  learning_objectives: "What you will learn",
  instructor: "Course instructor",
  who_its_for: "Who this is for",
  structure: "Course structure",
  quiz: "Course quiz",
  reviews: "Reviews & discussion",
};
const COURSE_SECTION_DEFAULT_ORDER = ["key_highlights", "learning_objectives", "instructor", "who_its_for", "structure", "quiz", "reviews"];

function SectionOrderInput({ order, visibility, labels, customSections, onOrderChange, onVisibilityChange }) {
  const safeOrder = (order && order.length) ? order : COURSE_SECTION_DEFAULT_ORDER;
  const safeVis = visibility || {};
  const customTitles = {};
  (customSections || []).forEach((s) => { customTitles[s.key] = s.title; });
  const displayName = (key) => (labels && labels[key]) || COURSE_SECTION_LABELS[key] || customTitles[key] || key;
  const move = (i, delta) => {
    const next = [...safeOrder];
    const target = i + delta;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    onOrderChange(next);
  };
  const toggle = (key) => {
    const current = safeVis[key] !== false;
    onVisibilityChange({ ...safeVis, [key]: !current });
  };
  return (
    <div className="space-y-2" data-testid="section-order-input">
      <Label>Page sections (visibility & order)</Label>
      <div className="rounded-lg border border-[#e8e0f0] bg-white/70 p-2">
        {safeOrder.map((key, i) => {
          const isVisible = safeVis[key] !== false;
          return (
            <div key={key} className="flex items-center justify-between gap-2 border-b border-[#f0e8f5] py-2 last:border-b-0">
              <label className="flex items-center gap-2 text-sm text-[#361a54]">
                <input type="checkbox" checked={isVisible} onChange={() => toggle(key)} data-testid={`section-toggle-${key}`} />
                <span className={isVisible ? "" : "text-muted-foreground line-through"}>{displayName(key)}</span>
              </label>
              <div className="flex items-center gap-1">
                <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="rounded border border-[#d4c5e6] bg-white px-2 py-0.5 text-xs text-[#361a54] hover:bg-[#f5f0fa] disabled:opacity-30" data-testid={`section-up-${key}`}>↑</button>
                <button type="button" disabled={i === safeOrder.length - 1} onClick={() => move(i, 1)} className="rounded border border-[#d4c5e6] bg-white px-2 py-0.5 text-xs text-[#361a54] hover:bg-[#f5f0fa] disabled:opacity-30" data-testid={`section-down-${key}`}>↓</button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">Uncheck to hide a section. Use ↑ / ↓ to reorder how sections appear on the course page.</p>
    </div>
  );
}

const RENAMEABLE_SECTIONS = [
  ["key_highlights", "Key Highlights"],
  ["learning_objectives", "What you will learn"],
  ["instructor", "Course instructor"],
  ["who_its_for", "Who this is for"],
  ["structure", "Course structure"],
  ["about", "About this course"],
];

function SectionTitlesInput({ labels, onChange }) {
  const safe = labels || {};
  return (
    <div className="space-y-2" data-testid="section-titles-input">
      <Label>Section titles (rename the box headers)</Label>
      <div className="grid gap-3 rounded-lg border border-[#e8e0f0] bg-white/70 p-3 md:grid-cols-2">
        {RENAMEABLE_SECTIONS.map(([key, def]) => (
          <div key={key} className="space-y-1">
            <span className="block text-xs text-muted-foreground">Default: {def}</span>
            <Input
              value={safe[key] || ""}
              placeholder={def}
              onChange={(e) => onChange({ ...safe, [key]: e.target.value })}
              data-testid={`section-title-${key}`}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Leave a field blank to keep the default title.</p>
    </div>
  );
}

function CustomSectionsInput({ sections, order, visibility, onChange, onOrderChange, onVisibilityChange }) {
  const list = sections || [];
  const baseOrder = (order && order.length) ? order : COURSE_SECTION_DEFAULT_ORDER;
  const add = () => {
    const key = `custom_${Math.random().toString(36).slice(2, 8)}`;
    onChange([...list, { key, title: "", items: [] }]);
    onOrderChange([...baseOrder, key]);
    onVisibilityChange({ ...(visibility || {}), [key]: true });
  };
  const update = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => {
    const sec = list[i];
    onChange(list.filter((_, idx) => idx !== i));
    if (sec) {
      onOrderChange(baseOrder.filter((k) => k !== sec.key));
      const nv = { ...(visibility || {}) };
      delete nv[sec.key];
      onVisibilityChange(nv);
    }
  };
  return (
    <div className="space-y-2" data-testid="custom-sections-input">
      <div className="flex items-center justify-between">
        <Label>Custom sections</Label>
        <Button type="button" size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={add} data-testid="add-custom-section-btn">
          + Add custom section
        </Button>
      </div>
      {list.length === 0 && (
        <p className="text-xs text-muted-foreground">No custom sections yet. Add one to show an extra bulleted box on this course page (it will appear in the section order list above).</p>
      )}
      {list.map((s, i) => (
        <div key={s.key} className="space-y-2 rounded-md border border-[#e8e0f0] bg-white/80 p-2" data-testid={`custom-section-${i}`}>
          <div className="space-y-1">
            <Label>Section title</Label>
            <Input value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })} data-testid={`custom-section-title-${i}`} />
          </div>
          <ListInput label="Items (bulleted list)" value={s.items || []} onChange={(v) => update(i, { items: v })} />
          <div className="flex justify-end">
            <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => remove(i)} data-testid={`remove-custom-section-${i}`}>
              Remove section
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------- Quiz Editor ----------------
function QuizEditor({ value, onChange }) {
  const quiz = value || { enabled: false, passing_score: 70, questions: [] };
  const setEnabled = (e) => onChange({ ...quiz, enabled: e });
  const setScore = (s) => onChange({ ...quiz, passing_score: Math.max(0, Math.min(100, Number(s) || 0)) });
  const addQuestion = () => {
    const qid = `q_${Math.random().toString(36).slice(2, 10)}`;
    onChange({
      ...quiz,
      questions: [...(quiz.questions || []), {
        question_id: qid,
        prompt: "",
        options: [
          { option_id: `${qid}_a`, text: "", is_correct: true },
          { option_id: `${qid}_b`, text: "", is_correct: false },
        ],
      }],
    });
  };
  const removeQuestion = (qi) => onChange({ ...quiz, questions: quiz.questions.filter((_, i) => i !== qi) });
  const updateQuestion = (qi, patch) => {
    const next = [...quiz.questions];
    next[qi] = { ...next[qi], ...patch };
    onChange({ ...quiz, questions: next });
  };
  const updateOption = (qi, oi, patch) => {
    const next = [...quiz.questions];
    const opts = [...next[qi].options];
    opts[oi] = { ...opts[oi], ...patch };
    // single-correct enforcement: if marking is_correct=true, unset others
    if (patch.is_correct === true) {
      opts.forEach((o, idx) => { if (idx !== oi) opts[idx] = { ...o, is_correct: false }; });
    }
    next[qi] = { ...next[qi], options: opts };
    onChange({ ...quiz, questions: next });
  };
  const addOption = (qi) => {
    const next = [...quiz.questions];
    const q = next[qi];
    next[qi] = {
      ...q,
      options: [...q.options, { option_id: `${q.question_id}_${String.fromCharCode(97 + q.options.length)}`, text: "", is_correct: false }],
    };
    onChange({ ...quiz, questions: next });
  };
  const removeOption = (qi, oi) => {
    const next = [...quiz.questions];
    next[qi] = { ...next[qi], options: next[qi].options.filter((_, i) => i !== oi) };
    onChange({ ...quiz, questions: next });
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#e8e0f0] bg-[#f5f0fa]/30 p-3" data-testid="quiz-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-semibold text-[#361a54]">Course quiz</p>
          <p className="text-xs text-muted-foreground">Optional MCQ-based assessment. Pass to mark the course complete.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#361a54]">
          <input type="checkbox" checked={!!quiz.enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="quiz-enabled-toggle" />
          Enable quiz on this course
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-xs">Passing score (%)</Label>
        <Input type="number" min={0} max={100} step={1} className="w-24"
          value={quiz.passing_score ?? 70}
          onChange={(e) => setScore(e.target.value)}
          data-testid="quiz-passing-score"
        />
      </div>
      {(quiz.questions || []).map((q, qi) => (
        <div key={q.question_id} className="space-y-2 rounded-md border border-[#e8e0f0] bg-white/80 p-2" data-testid={`edit-quiz-q-${qi}`}>
          <div className="space-y-1">
            <Label>Question {qi + 1}</Label>
            <Textarea rows={2} value={q.prompt} onChange={(e) => updateQuestion(qi, { prompt: e.target.value })} data-testid={`edit-quiz-prompt-${qi}`} />
          </div>
          <div className="space-y-1">
            {(q.options || []).map((opt, oi) => (
              <div key={opt.option_id} className="flex items-center gap-2" data-testid={`edit-quiz-q-${qi}-opt-${oi}`}>
                <input type="radio" name={`q-correct-${qi}`} checked={!!opt.is_correct} onChange={() => updateOption(qi, oi, { is_correct: true })} aria-label="Mark correct" data-testid={`edit-quiz-q-${qi}-correct-${oi}`} />
                <Input value={opt.text} placeholder={`Option ${String.fromCharCode(65 + oi)}`} onChange={(e) => updateOption(qi, oi, { text: e.target.value })} />
                <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => removeOption(qi, oi)}>Remove</button>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => addOption(qi)}>
              + Add option
            </Button>
          </div>
          <div className="flex justify-end">
            <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => removeQuestion(qi)} data-testid={`remove-quiz-q-${qi}`}>
              Remove question
            </button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={addQuestion} data-testid="add-quiz-question-btn">
        + Add question
      </Button>
    </div>
  );
}

// ---------------- Courses Admin ----------------
function CoursesAdmin() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  const load = async () => {
    const { data } = await apiClient.get("/courses");
    setCourses(data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({
    course_id: "",
    title: "",
    short_description: "",
    duration: "",
    status: "Available now",
    subtitle: "",
    overview: [],
    learning_objectives: [],
    who_its_for: [],
    structure: [],
    key_highlights: [],
    articulate_url: "",
    instructor_name: "",
    instructor_bio: "",
    instructor_photo_url: "",
    instructors: [],
    quiz: { enabled: false, passing_score: 70, questions: [] },
    section_order: COURSE_SECTION_DEFAULT_ORDER,
    section_visibility: {},
    section_labels: {},
    custom_sections: [],
    total_modules: 0,
    _isNew: true,
  });

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      const isNew = body._isNew;
      delete body._isNew;
      if (isNew) {
        await apiClient.post("/admin/courses", body);
      } else {
        await apiClient.put(`/admin/courses/${body.course_id}`, body);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete course ${id}? This will also remove all reviews/comments/progress.`)) return;
    await apiClient.delete(`/admin/courses/${id}`);
    await load();
  };

  const uploadContent = async (id, file) => {
    if (!file) return;
    setUploading(id);
    setUploadProgress({ courseId: id, percent: 0, label: "Preparing…" });
    setError("");
    // 1 MB chunks — stays under the default nginx client_max_body_size (1 MB)
    // so the production proxy never rejects chunks with a 413.
    const CHUNK = 1 * 1024 * 1024;
    try {
      // 1. init — do NOT set Content-Type manually; let axios/browser attach the
      //    multipart boundary automatically when it detects a FormData body.
      const initFd = new FormData();
      initFd.append("filename", file.name);
      const initRes = await apiClient.post(`/admin/courses/${id}/content/upload/init`, initFd, {
        timeout: 0,
      });
      const upload_id = initRes.data.upload_id;
      const total_chunks = Math.max(1, Math.ceil(file.size / CHUNK));

      // 2. chunks (sequential to keep order)
      for (let i = 0; i < total_chunks; i++) {
        const start = i * CHUNK;
        const end = Math.min(file.size, start + CHUNK);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("upload_id", upload_id);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(total_chunks));
        fd.append("file", blob, `chunk_${i}`);
        await apiClient.post(`/admin/courses/${id}/content/upload/chunk`, fd, {
          timeout: 0,
        });
        const percent = Math.round(((i + 1) / total_chunks) * 100);
        setUploadProgress({ courseId: id, percent, label: `Uploading chunk ${i + 1} / ${total_chunks}` });
      }

      // 3. complete — server finalises in the BACKGROUND and returns immediately.
      //    We then poll the status endpoint so large many-file courses never hit
      //    the proxy/Cloudflare request timeout.
      setUploadProgress({ courseId: id, percent: 100, label: "Finalising (unzipping on server)…" });
      const compFd = new FormData();
      compFd.append("upload_id", upload_id);
      compFd.append("total_chunks", String(total_chunks));
      await apiClient.post(`/admin/courses/${id}/content/upload/complete`, compFd, {
        timeout: 0,
      });

      // Poll processing status (up to ~10 minutes).
      let detected = 0;
      const deadline = Date.now() + 10 * 60 * 1000;
      for (;;) {
        await new Promise((r) => setTimeout(r, 2500));
        const st = await apiClient.get(`/admin/courses/${id}/content/upload/status`, {
          params: { upload_id },
          timeout: 0,
        });
        const job = st.data || {};
        if (job.status === "done") {
          detected = job.detected_modules || 0;
          break;
        }
        if (job.status === "error") {
          throw new Error(job.detail || "Server failed to process the uploaded course.");
        }
        setUploadProgress({ courseId: id, percent: 100, label: "Finalising on server… (this can take a minute or two for large courses)" });
        if (Date.now() > deadline) {
          throw new Error("Processing is taking longer than expected. Please refresh in a minute to check.");
        }
      }
      if (detected >= 1) {
        setUploadProgress({ courseId: id, percent: 100, label: `Done · auto-detected ${detected} modules from SCORM manifest` });
        await new Promise((r) => setTimeout(r, 1800));
      }
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      if (detail) {
        setError(formatApiErrorDetail(detail));
      } else if (status === 413) {
        setError("Upload chunk too large — please try again. Contact support if this persists.");
      } else if (status) {
        setError(`Upload failed (HTTP ${status}). Please try again.`);
      } else {
        setError(e.message || "Upload failed. Check your connection and try again.");
      }
    } finally {
      setUploading(null);
      setUploadProgress(null);
    }
  };

  const clearContent = async (id) => {
    if (!window.confirm("Remove uploaded course content?")) return;
    await apiClient.delete(`/admin/courses/${id}/content`);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#361a54]">Courses</h3>
        <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={() => setEditing(blank())} data-testid="add-course-btn">
          Add course
        </Button>
      </div>

      {error && <p className="text-sm text-red-700" data-testid="course-error">{error}</p>}

      <div className="grid gap-3">
        {courses.map((c) => (
          <Card key={c.course_id} className="card-elevated" data-testid={`admin-course-${c.course_id}`}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-semibold text-[#361a54]">{c.title}</CardTitle>
                  <CardDescription className="text-xs">{c.course_id} · {c.status}</CardDescription>
                  {c.content_entry_path ? (
                    <p className="text-xs text-emerald-800/80">Content uploaded: {c.content_filename || c.content_entry_path}</p>
                  ) : (
                    <p className="text-xs text-amber-900/80">No course content uploaded</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <label className="cursor-pointer rounded-md border border-[#d4c5e6] bg-white px-3 py-1 text-xs font-medium text-[#361a54] hover:bg-[#f5f0fa]">
                    {uploading === c.course_id ? "Uploading…" : "Upload .web zip"}
                    <input type="file" accept=".zip,.web,application/zip" className="hidden" disabled={uploading === c.course_id}
                      onChange={(e) => uploadContent(c.course_id, e.target.files?.[0])}
                      data-testid={`upload-content-${c.course_id}`} />
                  </label>
                  {uploadProgress && uploadProgress.courseId === c.course_id && (
                    <div className="flex w-full items-center gap-2 pt-1" data-testid={`upload-progress-${c.course_id}`}>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#f5f0fa]">
                        <div className="h-full bg-[#361a54] transition-[width]" style={{ width: `${uploadProgress.percent}%` }} />
                      </div>
                      <span className="text-xs text-[#4a2d73]">{uploadProgress.percent}% · {uploadProgress.label}</span>
                    </div>
                  )}
                  {c.content_entry_path && (
                    <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => clearContent(c.course_id)}>
                      Clear content
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing({ ...c, _isNew: false })} data-testid={`edit-course-${c.course_id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 bg-white text-red-700 hover:bg-red-50" onClick={() => remove(c.course_id)} data-testid={`delete-course-${c.course_id}`}>
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {editing && (
        <Card className="card-elevated" data-testid="course-editor">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-[#361a54]">{editing._isNew ? "New course" : "Edit course"}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label>Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="edit-course-title" />
            </div>
            <div className="space-y-1">
              <Label>Course ID (slug)</Label>
              <Input value={editing.course_id} onChange={(e) => setEditing({ ...editing, course_id: e.target.value })} disabled={!editing._isNew} data-testid="edit-course-id" />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Input value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Duration</Label>
              <Input value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Articulate URL (optional)</Label>
              <Input value={editing.articulate_url} onChange={(e) => setEditing({ ...editing, articulate_url: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Total modules / slides <span className="text-xs font-normal text-muted-foreground">(for % progress)</span></Label>
              <Input
                type="number"
                min={0}
                value={editing.total_modules || 0}
                onChange={(e) => setEditing({ ...editing, total_modules: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                data-testid="edit-course-total-modules"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Short description</Label>
              <Textarea rows={2} value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <ListInput label="Learning objectives" value={editing.learning_objectives} onChange={(v) => setEditing({ ...editing, learning_objectives: v })} />
            </div>
            <div className="md:col-span-2">
              <ListInput label="Who this is for" value={editing.who_its_for} onChange={(v) => setEditing({ ...editing, who_its_for: v })} />
            </div>
            <div className="md:col-span-2">
              <ListInput label="Key highlights" value={editing.key_highlights} onChange={(v) => setEditing({ ...editing, key_highlights: v })} />
            </div>
            <div className="md:col-span-2">
              <StructureInput value={editing.structure} onChange={(v) => setEditing({ ...editing, structure: v })} />
            </div>
            <div className="md:col-span-2 space-y-3 rounded-lg border border-[#e8e0f0] bg-[#f5f0fa]/30 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-[#361a54]">Course instructors</p>
                <Button type="button" size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => {
                  const next = [...(editing.instructors || [])];
                  next.push({ name: "", bio: "", photo_url: "" });
                  setEditing({ ...editing, instructors: next });
                }} data-testid="add-instructor-btn">
                  + Add instructor
                </Button>
              </div>
              {(editing.instructors || []).length === 0 && (
                <p className="text-xs text-muted-foreground">No instructors added yet. Click "Add instructor" to add one or more — they will appear stacked vertically on the course page.</p>
              )}
              {(editing.instructors || []).map((ins, idx) => (
                <div key={idx} className="space-y-2 rounded-md border border-[#e8e0f0] bg-white/80 p-2" data-testid={`edit-instructor-${idx}`}>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1">
                      <Label>Instructor {idx + 1} name</Label>
                      <Input value={ins.name || ""} onChange={(e) => {
                        const next = [...editing.instructors];
                        next[idx] = { ...next[idx], name: e.target.value };
                        setEditing({ ...editing, instructors: next });
                      }} data-testid={`edit-instructor-name-${idx}`} />
                    </div>
                    <ImageUploadField
                      label="Instructor photo"
                      value={ins.photo_url || ""}
                      onChange={(v) => {
                        const next = [...editing.instructors];
                        next[idx] = { ...next[idx], photo_url: v };
                        setEditing({ ...editing, instructors: next });
                      }}
                      testId={`edit-instructor-photo-${idx}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Instructor bio</Label>
                    <Textarea rows={3} value={ins.bio || ""} onChange={(e) => {
                      const next = [...editing.instructors];
                      next[idx] = { ...next[idx], bio: e.target.value };
                      setEditing({ ...editing, instructors: next });
                    }} data-testid={`edit-instructor-bio-${idx}`} />
                  </div>
                  <div className="flex justify-end">
                    <button type="button" className="text-xs text-red-700 hover:underline" onClick={() => {
                      const next = (editing.instructors || []).filter((_, i) => i !== idx);
                      setEditing({ ...editing, instructors: next });
                    }} data-testid={`remove-instructor-${idx}`}>
                      Remove instructor
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="md:col-span-2">
              <QuizEditor value={editing.quiz} onChange={(v) => setEditing({ ...editing, quiz: v })} />
            </div>
            <div className="md:col-span-2">
              <SectionTitlesInput
                labels={editing.section_labels}
                onChange={(v) => setEditing({ ...editing, section_labels: v })}
              />
            </div>
            <div className="md:col-span-2">
              <CustomSectionsInput
                sections={editing.custom_sections}
                order={editing.section_order}
                visibility={editing.section_visibility}
                onChange={(v) => setEditing((prev) => ({ ...prev, custom_sections: v }))}
                onOrderChange={(v) => setEditing((prev) => ({ ...prev, section_order: v }))}
                onVisibilityChange={(v) => setEditing((prev) => ({ ...prev, section_visibility: v }))}
              />
            </div>
            <div className="md:col-span-2">
              <SectionOrderInput
                order={editing.section_order}
                visibility={editing.section_visibility}
                labels={editing.section_labels}
                customSections={editing.custom_sections}
                onOrderChange={(v) => setEditing((prev) => ({ ...prev, section_order: v }))}
                onVisibilityChange={(v) => setEditing((prev) => ({ ...prev, section_visibility: v }))}
              />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="button" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={save} data-testid="save-course-btn">
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Case Studies Admin ----------------
function CaseStudiesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(null);

  const load = async () => {
    const { data } = await apiClient.get("/case-studies");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({
    case_id: "", title: "", organisation: "IIPP", theme: "", country: "", region: "", year: "N/A",
    summary: "", detail: [], focus: "", youtube_id: "", pdf_url: "", access_mode: "pdf", tags: [],
    author_name: "", author_affiliation: "", author_photo_url: "",
    _isNew: true,
  });

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      const isNew = body._isNew;
      delete body._isNew;
      if (isNew) await apiClient.post("/admin/case-studies", body);
      else await apiClient.put(`/admin/case-studies/${body.case_id}`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(`Delete case study ${id}?`)) return;
    await apiClient.delete(`/admin/case-studies/${id}`);
    await load();
  };

  const uploadVideo = async (id, file) => {
    if (!file) return;
    setUploading(id);
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await apiClient.post(`/admin/case-studies/${id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(null);
    }
  };

  const clearVideo = async (id) => {
    if (!window.confirm("Remove uploaded video?")) return;
    await apiClient.delete(`/admin/case-studies/${id}/video`);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#361a54]">Case studies</h3>
        <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={() => setEditing(blank())} data-testid="add-case-btn">
          Add case study
        </Button>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.case_id} className="card-elevated" data-testid={`admin-case-${c.case_id}`}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-semibold text-[#361a54]">{c.title}</CardTitle>
                  <CardDescription className="text-xs">{c.case_id} · {c.theme} · {c.country}</CardDescription>
                  <p className="text-xs text-muted-foreground">Access: <span className="font-medium">{c.access_mode}</span></p>
                  {c.video_storage_path && <p className="text-xs text-emerald-800/80">Video uploaded: {c.video_filename}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <label className="cursor-pointer rounded-md border border-[#d4c5e6] bg-white px-3 py-1 text-xs font-medium text-[#361a54] hover:bg-[#f5f0fa]">
                    {uploading === c.case_id ? "Uploading…" : "Upload video"}
                    <input type="file" accept="video/*" className="hidden" disabled={uploading === c.case_id}
                      onChange={(e) => uploadVideo(c.case_id, e.target.files?.[0])} />
                  </label>
                  {c.video_storage_path && (
                    <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => clearVideo(c.case_id)}>
                      Clear video
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing({ ...c, _isNew: false })} data-testid={`edit-case-${c.case_id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 bg-white text-red-700 hover:bg-red-50" onClick={() => remove(c.case_id)} data-testid={`delete-case-${c.case_id}`}>
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {editing && (
        <Card className="card-elevated" data-testid="case-editor">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-[#361a54]">{editing._isNew ? "New case study" : "Edit case study"}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label>Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Case ID (slug)</Label>
              <Input value={editing.case_id} onChange={(e) => setEditing({ ...editing, case_id: e.target.value })} disabled={!editing._isNew} />
            </div>
            <div className="space-y-1">
              <Label>Organisation</Label>
              <Input value={editing.organisation} onChange={(e) => setEditing({ ...editing, organisation: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Theme</Label>
              <Input value={editing.theme} onChange={(e) => setEditing({ ...editing, theme: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Country</Label>
              <Input value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Region</Label>
              <Input value={editing.region} onChange={(e) => setEditing({ ...editing, region: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Year</Label>
              <Input value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Access mode</Label>
              <Select value={editing.access_mode} onValueChange={(v) => setEditing({ ...editing, access_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">Open access (PDF link)</SelectItem>
                  <SelectItem value="request">On request</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Summary</Label>
              <Textarea rows={2} value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Focus</Label>
              <Input value={editing.focus} onChange={(e) => setEditing({ ...editing, focus: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>YouTube ID (optional)</Label>
              <Input value={editing.youtube_id} onChange={(e) => setEditing({ ...editing, youtube_id: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>PDF URL</Label>
              <Input value={editing.pdf_url} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <DetailInput value={editing.detail} onChange={(v) => setEditing({ ...editing, detail: v })} />
            </div>
            <div className="md:col-span-2 space-y-2 rounded-lg border border-[#e8e0f0] bg-[#f5f0fa]/30 p-3">
              <p className="text-sm font-semibold text-[#361a54]">Author</p>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Author name</Label>
                  <Input value={editing.author_name || ""} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} data-testid="edit-author-name" />
                </div>
                <div className="space-y-1">
                  <Label>Author affiliation</Label>
                  <Input value={editing.author_affiliation || ""} onChange={(e) => setEditing({ ...editing, author_affiliation: e.target.value })} data-testid="edit-author-affiliation" />
                </div>
              </div>
              <ImageUploadField
                label="Author photo (optional)"
                value={editing.author_photo_url || ""}
                onChange={(v) => setEditing({ ...editing, author_photo_url: v })}
                testId="edit-author-photo"
              />
            </div>
            <div className="md:col-span-2">
              <ListInput label="Tags" value={editing.tags} onChange={(v) => setEditing({ ...editing, tags: v })} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="button" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={save} data-testid="save-case-btn">
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Users Admin ----------------
function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const { data } = await apiClient.get("/admin/users", { params: q ? { q } : {} });
    setUsers(data);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This will remove their reviews, comments and progress too.`)) return;
    await apiClient.delete(`/admin/users/${id}`);
    await load();
  };

  const removeSelected = async () => {
    if (!window.confirm(`Delete ${selected.size} selected user(s)? This will permanently remove their reviews, comments and progress.`)) return;
    setDeleting(true);
    try {
      await apiClient.delete("/admin/users/batch", { data: { user_ids: [...selected] } });
      await load();
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (uid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map((u) => u.user_id)));
  };

  const setRole = async (id, role) => {
    await apiClient.put(`/admin/users/${id}/role`, null, { params: { role } });
    await load();
  };

  return (
    <div className="space-y-4" data-testid="users-admin">
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search by name or email" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="users-search-input" />
        <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={load} data-testid="users-search-btn">Search</Button>
        {selected.size > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="border-red-300 bg-white text-red-700 hover:bg-red-50"
            onClick={removeSelected}
            disabled={deleting}
            data-testid="bulk-delete-users-btn"
          >
            {deleting ? "Deleting…" : `Delete selected (${selected.size})`}
          </Button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#e8e0f0] bg-white/80">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f0fa] text-left text-xs uppercase tracking-[0.16em] text-[#4a2d73]">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={users.length > 0 && selected.size === users.length}
                  onChange={toggleAll}
                  data-testid="select-all-users-checkbox"
                  className="cursor-pointer"
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Organisation</th>
              <th className="p-3">Country</th>
              <th className="p-3">Auth</th>
              <th className="p-3">Role</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.user_id}
                className={`border-t border-[#e8e0f0] ${selected.has(u.user_id) ? "bg-[#f5f0fa]" : ""}`}
                data-testid={`user-row-${u.user_id}`}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selected.has(u.user_id)}
                    onChange={() => toggleSelect(u.user_id)}
                    data-testid={`select-user-${u.user_id}`}
                    className="cursor-pointer"
                  />
                </td>
                <td className="p-3 font-medium text-[#361a54]">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3">{u.organisation || "—"}</td>
                <td className="p-3">{u.country || "—"}</td>
                <td className="p-3"><span className="rounded-full bg-[#f5f0fa] px-2 py-0.5 text-xs text-[#361a54]">{u.auth_provider}</span></td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                    <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">user</SelectItem>
                      <SelectItem value="admin">admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" className="border-red-300 bg-white text-red-700 hover:bg-red-50" onClick={() => remove(u.user_id, u.email)} data-testid={`delete-user-${u.user_id}`}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Progress Admin ----------------
function ProgressAdmin() {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({ country: "all", course_id: "all", status: "all" });

  const load = async () => {
    const params = {};
    if (filters.country !== "all") params.country = filters.country;
    if (filters.course_id !== "all") params.course_id = filters.course_id;
    if (filters.status !== "all") params.status = filters.status;
    const [{ data: rs }, { data: cs }, { data: cts }] = await Promise.all([
      apiClient.get("/admin/progress", { params }),
      apiClient.get("/admin/countries"),
      apiClient.get("/courses"),
    ]);
    setRows(rs);
    setCountries(cs);
    setCourses(cts);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const exportXlsx = () => {
    const params = new URLSearchParams();
    if (filters.country !== "all") params.set("country", filters.country);
    if (filters.course_id !== "all") params.set("course_id", filters.course_id);
    if (filters.status !== "all") params.set("status", filters.status);
    const url = `${API}/admin/export/progress.xlsx${params.toString() ? "?" + params.toString() : ""}`;
    window.open(url, "_blank");
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="progress-admin">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[160px] space-y-1">
          <Label className="text-xs">Country</Label>
          <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v })}>
            <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All countries</SelectItem>
              {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[220px] space-y-1">
          <Label className="text-xs">Course</Label>
          <Select value={filters.course_id} onValueChange={(v) => setFilters({ ...filters, course_id: v })}>
            <SelectTrigger data-testid="filter-course"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map((c) => <SelectItem key={c.course_id} value={c.course_id}>{c.title}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px] space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="enrolled">Enrolled</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={exportXlsx} data-testid="export-progress-btn">
            Export .xlsx
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#e8e0f0] bg-white/80">
        <table className="w-full text-sm">
          <thead className="bg-[#f5f0fa] text-left text-xs uppercase tracking-[0.16em] text-[#4a2d73]">
            <tr>
              <th className="p-3">Learner</th>
              <th className="p-3">Email</th>
              <th className="p-3">Country</th>
              <th className="p-3">Organisation</th>
              <th className="p-3">Course</th>
              <th className="p-3">Status</th>
              <th className="p-3">Started</th>
              <th className="p-3">Completed</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No progress records match the filters.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="border-t border-[#e8e0f0]">
                <td className="p-3 font-medium text-[#361a54]">{r.user_name}</td>
                <td className="p-3">{r.user_email}</td>
                <td className="p-3">{r.country || "—"}</td>
                <td className="p-3">{r.organisation || "—"}</td>
                <td className="p-3">{r.course_title}</td>
                <td className="p-3"><span className="rounded-full bg-[#f5f0fa] px-2 py-0.5 text-xs text-[#361a54]">{r.status}</span></td>
                <td className="p-3">{fmt(r.started_at)}</td>
                <td className="p-3">{fmt(r.completed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------- Blog Admin ----------------
function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await apiClient.get("/blog");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ slug: "", title: "", excerpt: "", body: "", author_name: "", published_at: "", _isNew: true });

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      const isNew = body._isNew;
      delete body._isNew;
      if (!body.published_at) delete body.published_at;
      if (isNew) await apiClient.post("/admin/blog", body);
      else await apiClient.put(`/admin/blog/${body.slug}`, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete post ${slug}?`)) return;
    await apiClient.delete(`/admin/blog/${slug}`);
    await load();
  };

  return (
    <div className="space-y-4" data-testid="blog-admin">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#361a54]">Blog posts</h3>
        <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={() => setEditing(blank())} data-testid="add-blog-btn">
          Add post
        </Button>
      </div>
      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="grid gap-3">
        {posts.map((p) => (
          <Card key={p.slug} className="card-elevated" data-testid={`admin-blog-${p.slug}`}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle className="text-sm font-semibold text-[#361a54]">{p.title}</CardTitle>
                  <CardDescription className="text-xs">{p.slug} · {p.author_name || "—"}</CardDescription>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing({ ...p, _isNew: false })} data-testid={`edit-blog-${p.slug}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-300 bg-white text-red-700 hover:bg-red-50" onClick={() => remove(p.slug)} data-testid={`delete-blog-${p.slug}`}>
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {editing && (
        <Card className="card-elevated" data-testid="blog-editor">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-[#361a54]">{editing._isNew ? "New post" : "Edit post"}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1 md:col-span-2">
              <Label>Title</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Slug</Label>
              <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} disabled={!editing._isNew} />
            </div>
            <div className="space-y-1">
              <Label>Author name</Label>
              <Input value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Excerpt</Label>
              <Input value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Body</Label>
              <Textarea rows={10} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
            </div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <Button type="button" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="button" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={save} data-testid="save-blog-btn">
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------- Analytics Admin ----------------
function AnalyticsAdmin() {
  const [overview, setOverview] = useState(null);
  const [courses, setCourses] = useState([]);
  const [cases, setCases] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: o }, { data: c }, { data: cs }, { data: u }] = await Promise.all([
        apiClient.get("/admin/analytics/overview"),
        apiClient.get("/admin/analytics/courses"),
        apiClient.get("/admin/analytics/cases"),
        apiClient.get("/admin/analytics/users"),
      ]);
      setOverview(o);
      setCourses(c);
      setCases(cs);
      setUsers(u);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading analytics…</p>;

  const stat = (label, value, testId) => (
    <Card className="card-elevated" data-testid={testId}>
      <CardContent className="px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#4a2d73]">{label}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-[#361a54]">{value}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="analytics-admin">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-[#361a54]">Overview</h3>
        <Button size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" onClick={load} data-testid="analytics-refresh-btn">
          Refresh
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
        {stat("Users", overview.users, "stat-users")}
        {stat("Courses", overview.courses, "stat-courses")}
        {stat("Case studies", overview.case_studies, "stat-cases")}
        {stat("Blog posts", overview.blog_posts, "stat-blog")}
        {stat("Course views", overview.course_views, "stat-course-views")}
        {stat("Course completions", overview.progress_completed, "stat-completions")}
        {stat("Case views", overview.case_views, "stat-case-views")}
        {stat("PDF downloads", overview.case_downloads, "stat-pdf-downloads")}
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold text-[#361a54]">Courses</h3>
        <div className="overflow-x-auto rounded-xl border border-[#e8e0f0] bg-white/80">
          <table className="w-full text-sm" data-testid="analytics-courses-table">
            <thead className="bg-[#f5f0fa] text-left text-xs uppercase tracking-[0.16em] text-[#4a2d73]">
              <tr>
                <th className="p-3">Course</th>
                <th className="p-3">Views</th>
                <th className="p-3">Starts</th>
                <th className="p-3">Enrolled</th>
                <th className="p-3">In progress</th>
                <th className="p-3">Completed</th>
                <th className="p-3">Reviews</th>
                <th className="p-3">Avg rating</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((c) => (
                <tr key={c.course_id} className="border-t border-[#e8e0f0]" data-testid={`analytics-course-${c.course_id}`}>
                  <td className="p-3 font-medium text-[#361a54]">{c.title}</td>
                  <td className="p-3 tabular-nums">{c.views}</td>
                  <td className="p-3 tabular-nums">{c.starts}</td>
                  <td className="p-3 tabular-nums">{c.enrolled}</td>
                  <td className="p-3 tabular-nums">{c.in_progress}</td>
                  <td className="p-3 tabular-nums">{c.completed}</td>
                  <td className="p-3 tabular-nums">{c.review_count}</td>
                  <td className="p-3 tabular-nums">{c.average_rating || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold text-[#361a54]">Case studies</h3>
        <div className="overflow-x-auto rounded-xl border border-[#e8e0f0] bg-white/80">
          <table className="w-full text-sm" data-testid="analytics-cases-table">
            <thead className="bg-[#f5f0fa] text-left text-xs uppercase tracking-[0.16em] text-[#4a2d73]">
              <tr>
                <th className="p-3">Case</th>
                <th className="p-3">Country</th>
                <th className="p-3">Theme</th>
                <th className="p-3">Access</th>
                <th className="p-3">Views</th>
                <th className="p-3">PDF clicks</th>
                <th className="p-3">Access requests</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.case_id} className="border-t border-[#e8e0f0]" data-testid={`analytics-case-${c.case_id}`}>
                  <td className="p-3 font-medium text-[#361a54]">{c.title}</td>
                  <td className="p-3">{c.country || "—"}</td>
                  <td className="p-3">{c.theme || "—"}</td>
                  <td className="p-3">
                    <span className="rounded-full bg-[#f5f0fa] px-2 py-0.5 text-xs text-[#361a54]">
                      {c.access_mode === "pdf" ? "Open access" : "On request"}
                    </span>
                  </td>
                  <td className="p-3 tabular-nums">{c.views}</td>
                  <td className="p-3 tabular-nums">{c.downloads}</td>
                  <td className="p-3 tabular-nums">{c.access_requests}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-base font-semibold text-[#361a54]">Users</h3>
        <div className="overflow-x-auto rounded-xl border border-[#e8e0f0] bg-white/80">
          <table className="w-full text-sm" data-testid="analytics-users-table">
            <thead className="bg-[#f5f0fa] text-left text-xs uppercase tracking-[0.16em] text-[#4a2d73]">
              <tr>
                <th className="p-3">Learner</th>
                <th className="p-3">Email</th>
                <th className="p-3">Country</th>
                <th className="p-3">Completed</th>
                <th className="p-3">In progress</th>
                <th className="p-3">Case views</th>
                <th className="p-3">PDF clicks</th>
                <th className="p-3">Access reqs</th>
                <th className="p-3">Last active</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-[#e8e0f0]" data-testid={`analytics-user-${u.user_id}`}>
                  <td className="p-3 font-medium text-[#361a54]">{u.name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3">{u.country || "—"}</td>
                  <td className="p-3 tabular-nums">{u.courses_completed}</td>
                  <td className="p-3 tabular-nums">{u.courses_in_progress}</td>
                  <td className="p-3 tabular-nums">{u.case_views}</td>
                  <td className="p-3 tabular-nums">{u.case_downloads}</td>
                  <td className="p-3 tabular-nums">{u.access_requests}</td>
                  <td className="p-3 text-xs text-slate-600">{fmt(u.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SettingsAdmin() {
  const [values, setValues] = useState({ workshop_text: "", about_dpi_governance: "", site_logo: "", certificate_logo: "", certificate_heading: "", certificate_body: "", certificate_signature: "", coming_soon_courses: "", coming_soon_features: "" });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [err, setErr] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [certLogoUploading, setCertLogoUploading] = useState(false);

  useEffect(() => {
    apiClient.get("/settings").then(({ data }) => {
      setValues({
        workshop_text: data.workshop_text || "",
        about_dpi_governance: data.about_dpi_governance || "",
        site_logo: data.site_logo || "",
        certificate_logo: data.certificate_logo || "",
        certificate_heading: data.certificate_heading || "",
        certificate_body: data.certificate_body || "",
        certificate_signature: data.certificate_signature || "",
        coming_soon_courses: data.coming_soon_courses || "",
        coming_soon_features: data.coming_soon_features || "",
      });
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const save = async (key) => {
    setSavingKey(key);
    setErr("");
    try {
      await apiClient.put(`/admin/settings/${key}`, { value: values[key] });
      setSavedKey(key);
      setTimeout(() => setSavedKey(""), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingKey("");
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/admin/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // data.url = "/api/images/<filename>" — build an absolute URL using the API base
      const fullUrl = API + data.url.replace(/^\/api/, "");
      await apiClient.put("/admin/settings/site_logo", { value: fullUrl });
      setValues((v) => ({ ...v, site_logo: fullUrl }));
      setSavedKey("site_logo");
      setTimeout(() => setSavedKey(""), 3000);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await apiClient.put("/admin/settings/site_logo", { value: "" });
      setValues((v) => ({ ...v, site_logo: "" }));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const uploadCertLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCertLogoUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/admin/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = API + data.url.replace(/^\/api/, "");
      await apiClient.put("/admin/settings/certificate_logo", { value: fullUrl });
      setValues((v) => ({ ...v, certificate_logo: fullUrl }));
      setSavedKey("certificate_logo");
      setTimeout(() => setSavedKey(""), 3000);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCertLogoUploading(false);
      e.target.value = "";
    }
  };

  const removeCertLogo = async () => {
    try {
      await apiClient.put("/admin/settings/certificate_logo", { value: "" });
      setValues((v) => ({ ...v, certificate_logo: "" }));
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading settings…</p>;

  return (
    <div className="space-y-6" data-testid="settings-admin">
      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="settings-error">{err}</p>
      )}

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Site Logo</CardTitle>
          <CardDescription className="text-sm">Upload a new logo to replace the default one shown in the header and footer. PNG, JPEG or WEBP, max 5 MB.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {values.site_logo ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Current logo:</p>
              <img src={values.site_logo} alt="Current site logo" className="h-14 w-auto rounded object-contain border border-[#e8e0f0] p-1" data-testid="current-logo-preview" />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No custom logo set — using the default bundled logo.</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#d4c5e6] bg-white px-3 py-1.5 text-sm text-[#361a54] hover:bg-[#f5f0fa]">
              {logoUploading ? "Uploading…" : "Choose file"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={uploadLogo}
                disabled={logoUploading}
                className="sr-only"
                data-testid="logo-upload-input"
              />
            </label>
            {savedKey === "site_logo" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="logo-saved-indicator">Logo updated.</span>
            )}
            {values.site_logo && (
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 bg-white text-red-700 hover:bg-red-50"
                onClick={removeLogo}
                data-testid="remove-logo-btn"
              >
                Remove (use default)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Certificate Logo</CardTitle>
          <CardDescription className="text-sm">Upload a logo shown at the top of the auto-generated PDF certificate of completion. PNG, JPEG or WEBP, max 5 MB. Leave empty to use the default bundled logo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {values.certificate_logo ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Current certificate logo:</p>
              <img src={values.certificate_logo} alt="Current certificate logo" className="h-14 w-auto rounded object-contain border border-[#e8e0f0] p-1" data-testid="current-certificate-logo-preview" />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No custom certificate logo set — using the default bundled logo.</p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#d4c5e6] bg-white px-3 py-1.5 text-sm text-[#361a54] hover:bg-[#f5f0fa]">
              {certLogoUploading ? "Uploading…" : "Choose file"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={uploadCertLogo}
                disabled={certLogoUploading}
                className="sr-only"
                data-testid="certificate-logo-upload-input"
              />
            </label>
            {savedKey === "certificate_logo" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="certificate-logo-saved-indicator">Certificate logo updated.</span>
            )}
            {values.certificate_logo && (
              <Button
                size="sm"
                variant="outline"
                className="border-red-300 bg-white text-red-700 hover:bg-red-50"
                onClick={removeCertLogo}
                data-testid="remove-certificate-logo-btn"
              >
                Remove (use default)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Certificate Wording</CardTitle>
          <CardDescription className="text-sm">Edit the text on the auto-generated PDF certificate. The learner name, course title and date are filled in automatically. In the body you can use the placeholders <span className="font-mono text-[#361a54]">{"{{name}}"}</span>, <span className="font-mono text-[#361a54]">{"{{course}}"}</span> and <span className="font-mono text-[#361a54]">{"{{date}}"}</span>. Leave the signature blank to hide it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Heading</Label>
            <Input
              value={values.certificate_heading}
              onChange={(e) => setValues((v) => ({ ...v, certificate_heading: e.target.value }))}
              placeholder="Certificate of Completion"
              data-testid="setting-certificate-heading"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "certificate_heading"} onClick={() => save("certificate_heading")} data-testid="save-certificate-heading-btn">
                {savingKey === "certificate_heading" ? "Saving…" : "Save"}
              </Button>
              {savedKey === "certificate_heading" && <span className="text-xs font-medium text-emerald-700">Saved.</span>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Body line</Label>
            <Textarea
              value={values.certificate_body}
              onChange={(e) => setValues((v) => ({ ...v, certificate_body: e.target.value }))}
              rows={2}
              placeholder="has successfully completed the course"
              data-testid="setting-certificate-body"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "certificate_body"} onClick={() => save("certificate_body")} data-testid="save-certificate-body-btn">
                {savingKey === "certificate_body" ? "Saving…" : "Save"}
              </Button>
              {savedKey === "certificate_body" && <span className="text-xs font-medium text-emerald-700">Saved.</span>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Signature line</Label>
            <Input
              value={values.certificate_signature}
              onChange={(e) => setValues((v) => ({ ...v, certificate_signature: e.target.value }))}
              placeholder="DPI Governance Applied Learning Programme"
              data-testid="setting-certificate-signature"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "certificate_signature"} onClick={() => save("certificate_signature")} data-testid="save-certificate-signature-btn">
                {savingKey === "certificate_signature" ? "Saving…" : "Save"}
              </Button>
              {savedKey === "certificate_signature" && <span className="text-xs font-medium text-emerald-700">Saved.</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Learn DPI — In person training / workshop text</CardTitle>
          <CardDescription className="text-sm">Shown in the workshop box on the Learn DPI page, below e-learning courses.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={values.workshop_text}
            onChange={(e) => setValues((v) => ({ ...v, workshop_text: e.target.value }))}
            rows={6}
            placeholder="Describe in-person training / workshop offerings…"
            data-testid="setting-workshop-text"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "workshop_text"} onClick={() => save("workshop_text")} data-testid="save-workshop-text-btn">
              {savingKey === "workshop_text" ? "Saving…" : "Save"}
            </Button>
            {savedKey === "workshop_text" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="setting-saved-workshop">Saved.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">About page — DPI Governance description</CardTitle>
          <CardDescription className="text-sm">Shown in the highlighted box on the About page explaining DPI Governance and why we do this.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={values.about_dpi_governance}
            onChange={(e) => setValues((v) => ({ ...v, about_dpi_governance: e.target.value }))}
            rows={8}
            placeholder="Describe DPI Governance and why we do this…"
            data-testid="setting-about-dpi"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "about_dpi_governance"} onClick={() => save("about_dpi_governance")} data-testid="save-about-dpi-btn">
              {savingKey === "about_dpi_governance" ? "Saving…" : "Save"}
            </Button>
            {savedKey === "about_dpi_governance" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="setting-saved-about">Saved.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Coming soon — Courses in development</CardTitle>
          <CardDescription className="text-sm">Shown as a bulleted list on the Coming soon page. Enter one item per line.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={values.coming_soon_courses}
            onChange={(e) => setValues((v) => ({ ...v, coming_soon_courses: e.target.value }))}
            rows={6}
            placeholder={"One course per line, e.g.\nDPI Safeguards and risk management"}
            data-testid="setting-coming-soon-courses"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "coming_soon_courses"} onClick={() => save("coming_soon_courses")} data-testid="save-coming-soon-courses-btn">
              {savingKey === "coming_soon_courses" ? "Saving…" : "Save"}
            </Button>
            {savedKey === "coming_soon_courses" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="setting-saved-coming-soon-courses">Saved.</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-[#361a54]">Coming soon — Features in development</CardTitle>
          <CardDescription className="text-sm">Shown as a bulleted list on the Coming soon page. Enter one item per line.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={values.coming_soon_features}
            onChange={(e) => setValues((v) => ({ ...v, coming_soon_features: e.target.value }))}
            rows={6}
            placeholder={"One feature per line, e.g.\nVerifiable learner certificates"}
            data-testid="setting-coming-soon-features"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingKey === "coming_soon_features"} onClick={() => save("coming_soon_features")} data-testid="save-coming-soon-features-btn">
              {savingKey === "coming_soon_features" ? "Saving…" : "Save"}
            </Button>
            {savedKey === "coming_soon_features" && (
              <span className="text-xs font-medium text-emerald-700" data-testid="setting-saved-coming-soon-features">Saved.</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


export default function AdminDashboard() {
  return (
    <div data-testid="admin-dashboard" className="flex flex-1 flex-col gap-6">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#4a2d73]">Admin</p>
          <h1 className="text-balance text-2xl font-semibold tracking-tight text-[#361a54] md:text-3xl">Manage content & users</h1>
          <p className="mt-1 text-sm text-muted-foreground">Add or remove courses, case studies, blog posts and users. Upload Articulate (.web) packages and case study videos. Track learner progression with filters and export to Excel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="export-users-btn">
            <a href={`${API}/admin/export/users.xlsx`} target="_blank" rel="noreferrer">Export users</a>
          </Button>
          <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="export-courses-btn">
            <a href={`${API}/admin/export/courses.xlsx`} target="_blank" rel="noreferrer">Export courses</a>
          </Button>
          <Button asChild size="sm" variant="outline" className="border-[#d4c5e6] bg-white text-[#361a54] hover:bg-[#f5f0fa]" data-testid="export-cases-btn">
            <a href={`${API}/admin/export/case-studies.xlsx`} target="_blank" rel="noreferrer">Export case studies</a>
          </Button>
        </div>
      </section>
      <Tabs defaultValue="analytics" className="w-full">
        <TabsList>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">Courses</TabsTrigger>
          <TabsTrigger value="cases" data-testid="tab-cases">Case studies</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Progress</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="analytics" className="mt-4"><AnalyticsAdmin /></TabsContent>
        <TabsContent value="courses" className="mt-4"><CoursesAdmin /></TabsContent>
        <TabsContent value="cases" className="mt-4"><CaseStudiesAdmin /></TabsContent>
        <TabsContent value="progress" className="mt-4"><ProgressAdmin /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersAdmin /></TabsContent>
        <TabsContent value="blog" className="mt-4"><BlogAdmin /></TabsContent>
        <TabsContent value="settings" className="mt-4"><SettingsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
